import {
  useCallback,
  useEffect,
  useState,
  type MouseEvent as ReactMouseEvent,
} from 'react';

import type {
  LayoutPoint,
  ViewerEntityId,
  ViewerPosition,
} from '../contracts/models';
import {
  applyDrawingGeometricAssist,
  distanceBetweenLayoutPoints,
  findAreaTagDrawingSnap,
  getAreaTagDrawingSnapThreshold,
  normalizeAreaTagPolygon,
  type AreaTagDrawingPolygon,
  type AreaTagDrawingSnap,
  type DrawingLineAssist,
} from '../geometry/areaTagDrawing';

interface ClientPoint {
  clientX: number;
  clientY: number;
}

export interface UseAreaTagDrawingOptions<
  TId extends ViewerEntityId = ViewerEntityId,
> {
  enabled: boolean;
  polygons: readonly AreaTagDrawingPolygon<TId>[];
  scale: number;
  getContentPoint(event: ClientPoint): ViewerPosition | null;
  onComplete(points: readonly LayoutPoint[]): void;
  closeThreshold?: number;
}

export interface UseAreaTagDrawingResult<
  TId extends ViewerEntityId = ViewerEntityId,
> {
  points: readonly LayoutPoint[];
  hoverPoint: LayoutPoint | null;
  snap: AreaTagDrawingSnap<TId> | null;
  horizontalConstrained: boolean;
  verticalConstrained: boolean;
  parallelAssist: DrawingLineAssist | null;
  equalLengthAssist: DrawingLineAssist | null;
  handlePointerMove(event: ClientPoint): void;
  handlePointerClick(event: ClientPoint): void;
  clear(): void;
}

export const useAreaTagDrawing = <
  TId extends ViewerEntityId = ViewerEntityId,
>({
  enabled,
  polygons,
  scale,
  getContentPoint,
  onComplete,
  closeThreshold = 20,
}: UseAreaTagDrawingOptions<TId>): UseAreaTagDrawingResult<TId> => {
  const [points, setPoints] = useState<LayoutPoint[]>([]);
  const [hoverPoint, setHoverPoint] = useState<LayoutPoint | null>(
    null,
  );
  const [snap, setSnap] =
    useState<AreaTagDrawingSnap<TId> | null>(null);
  const [horizontalConstrained, setHorizontalConstrained] =
    useState(false);
  const [verticalConstrained, setVerticalConstrained] =
    useState(false);
  const [parallelEnabled, setParallelEnabled] = useState(false);
  const [equalLengthEnabled, setEqualLengthEnabled] =
    useState(false);
  const [parallelAssist, setParallelAssist] =
    useState<DrawingLineAssist | null>(null);
  const [equalLengthAssist, setEqualLengthAssist] =
    useState<DrawingLineAssist | null>(null);

  const clearAssistance = useCallback(() => {
    setHorizontalConstrained(false);
    setVerticalConstrained(false);
    setParallelEnabled(false);
    setEqualLengthEnabled(false);
    setParallelAssist(null);
    setEqualLengthAssist(null);
    setSnap(null);
  }, []);

  const clear = useCallback(() => {
    setPoints([]);
    setHoverPoint(null);
    clearAssistance();
  }, [clearAssistance]);

  const resolvePoint = useCallback(
    (
      event: ClientPoint,
    ): {
      point: LayoutPoint;
      parallel: DrawingLineAssist | null;
      equalLength: DrawingLineAssist | null;
    } | null => {
      const contentPoint = getContentPoint(event);
      if (!contentPoint) return null;
      let point: LayoutPoint = [contentPoint.x, contentPoint.y];
      let parallel: DrawingLineAssist | null = null;
      let equalLength: DrawingLineAssist | null = null;
      const lastPoint = points[points.length - 1];
      if (lastPoint) {
        if (horizontalConstrained) {
          point = [contentPoint.x, lastPoint[1]];
        } else if (verticalConstrained) {
          point = [lastPoint[0], contentPoint.y];
        } else {
          const assisted = applyDrawingGeometricAssist(
            point,
            points,
            {
              parallel: parallelEnabled,
              equalLength: equalLengthEnabled,
            },
          );
          point = assisted.point;
          parallel = assisted.parallel;
          equalLength = assisted.equalLength;
        }
      }
      return { point, parallel, equalLength };
    },
    [
      equalLengthEnabled,
      getContentPoint,
      horizontalConstrained,
      parallelEnabled,
      points,
      verticalConstrained,
    ],
  );

  const handlePointerMove = useCallback(
    (event: ClientPoint) => {
      if (!enabled) return;
      const resolved = resolvePoint(event);
      if (!resolved) return;
      setHoverPoint(resolved.point);
      setParallelAssist(resolved.parallel);
      setEqualLengthAssist(resolved.equalLength);
      if (resolved.parallel || resolved.equalLength) {
        setSnap(null);
        return;
      }
      setSnap(
        findAreaTagDrawingSnap(
          resolved.point,
          polygons,
          getAreaTagDrawingSnapThreshold(scale),
        ),
      );
    },
    [
      enabled,
      polygons,
      resolvePoint,
      scale,
    ],
  );

  const handlePointerClick = useCallback(
    (event: ClientPoint) => {
      if (!enabled) return;
      const resolved = resolvePoint(event);
      if (!resolved) return;
      const finalPoint = snap?.point ?? resolved.point;

      if (
        points.length >= 3 &&
        distanceBetweenLayoutPoints(finalPoint, points[0]) <
          closeThreshold
      ) {
        const completed = normalizeAreaTagPolygon(points);
        if (completed.length >= 3) onComplete(completed);
        clear();
        return;
      }
      setPoints((current) => [...current, finalPoint]);
    },
    [
      clear,
      closeThreshold,
      enabled,
      onComplete,
      points,
      resolvePoint,
      snap,
    ],
  );

  useEffect(() => {
    if (!enabled) clear();
  }, [clear, enabled]);

  useEffect(() => {
    if (!enabled) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === 'z' &&
        !event.shiftKey
      ) {
        event.preventDefault();
        setPoints((current) => current.slice(0, -1));
        return;
      }
      if (event.shiftKey && event.key.toLowerCase() === 'h') {
        event.preventDefault();
        setHorizontalConstrained(true);
        setVerticalConstrained(false);
        setParallelEnabled(false);
        setEqualLengthEnabled(false);
        return;
      }
      if (event.shiftKey && event.key.toLowerCase() === 'v') {
        event.preventDefault();
        setVerticalConstrained(true);
        setHorizontalConstrained(false);
        setParallelEnabled(false);
        setEqualLengthEnabled(false);
        return;
      }
      if (event.shiftKey && event.key.toLowerCase() === 'p') {
        event.preventDefault();
        setParallelEnabled(true);
        setHorizontalConstrained(false);
        setVerticalConstrained(false);
        return;
      }
      if (event.shiftKey && event.key.toLowerCase() === 'l') {
        event.preventDefault();
        setEqualLengthEnabled(true);
        setHorizontalConstrained(false);
        setVerticalConstrained(false);
        return;
      }
      if (event.key === 'Escape') clear();
    };
    const handleKeyUp = (event: KeyboardEvent) => {
      if (!event.shiftKey) clearAssistance();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [clear, clearAssistance, enabled]);

  return {
    points,
    hoverPoint,
    snap,
    horizontalConstrained,
    verticalConstrained,
    parallelAssist,
    equalLengthAssist,
    handlePointerMove,
    handlePointerClick,
    clear,
  };
};

export type AreaTagDrawingMouseEvent =
  | MouseEvent
  | ReactMouseEvent<SVGSVGElement>
  | ReactMouseEvent<HTMLDivElement>;
